import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  alpha,
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  ResponsiveContainer,
  Line,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { getAnalytics, getCompanies } from '../services/api';
import { neonBlue, shakespeare, kepple, stormGrey, redOrange } from '../theme/colors';

const chartBarColor = neonBlue[600];
const chartLineColor = redOrange[500];

const StatCard = ({ title, today, month, todayLabel = 'Today', monthLabel = 'This Month', icon: Icon, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      height: '100%',
      width: '100%',
      borderRadius: 2,
      border: `1px solid ${alpha(accent, 0.12)}`,
      background: `linear-gradient(135deg, ${alpha(accent, 0.06)} 0%, #ffffff 55%)`,
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': {
        boxShadow: `0 8px 24px ${alpha(accent, 0.18)}`,
        transform: 'translateY(-2px)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        bgcolor: accent,
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: '0.875rem', fontWeight: 600, color: stormGrey[800] }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(accent, 0.12),
          color: accent,
        }}
      >
        <Icon />
      </Box>
    </Stack>
    <Box display="flex" gap={2} sx={{ width: '100%' }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.06),
          border: `1px solid ${alpha(accent, 0.08)}`,
        }}
      >
        <Typography variant="caption" sx={{ color: stormGrey[500], fontWeight: 500 }}>
          {todayLabel}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 600, lineHeight: 1.2, color: accent, mt: 0.5 }}>
          {today}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: alpha(neonBlue[500], 0.04),
          border: `1px solid ${alpha(neonBlue[500], 0.08)}`,
        }}
      >
        <Typography variant="caption" sx={{ color: stormGrey[500], fontWeight: 500, whiteSpace: 'nowrap' }}>
          {monthLabel}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 600, lineHeight: 1.2, color: stormGrey[900], mt: 0.5 }}>
          {month}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const SectionHeading = ({ title, subtitle }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      variant="h5"
      sx={{
        fontWeight: 600,
        color: stormGrey[900],
        fontSize: '1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&::before': {
          content: '""',
          width: 4,
          height: 22,
          borderRadius: 1,
          bgcolor: neonBlue[500],
          display: 'inline-block',
        },
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, ml: 2 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const ChartPanel = ({ title, subtitle, children, height = 320 }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      height: '100%',
      borderRadius: 2,
      border: `1px solid ${stormGrey[200]}`,
      bgcolor: '#fff',
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: stormGrey[900], mb: 0.5 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    <Box sx={{ width: '100%', height }}>
      {children}
    </Box>
  </Paper>
);

const chartTooltipStyle = {
  borderRadius: 8,
  border: `1px solid ${stormGrey[200]}`,
  boxShadow: '0 8px 24px rgba(33, 38, 54, 0.12)',
};

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const buildYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2020;
  return Array.from({ length: currentYear - startYear + 1 }, (_, index) => currentYear - index);
};

const filterControlSx = {
  minWidth: 140,
  bgcolor: alpha('#fff', 0.95),
  borderRadius: 1,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
};

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const yearOptions = buildYearOptions();
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'super-admin';

  useEffect(() => {
    const fetchCompanies = async () => {
      if (isSuperAdmin) {
        try {
          const response = await getCompanies();
          setCompanies(response.data);
        } catch (err) {
          // ignore
        }
      }
    };

    fetchCompanies();
  }, [isSuperAdmin]);

  useEffect(() => {
    let isMounted = true;

    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const response = await getAnalytics(
          'dashboard',
          selectedCompany || undefined,
          { year: Number(selectedYear), month: Number(selectedMonth) }
        );
        if (isMounted) {
          setAnalytics(response.data);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.response?.data?.message || 'Error fetching analytics');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 300000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [selectedCompany, selectedMonth, selectedYear]);

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    setSelectedCompany(companyId);
    const company = companies.find((c) => c.id === companyId);
    setSelectedCompanyName(company ? company.name : '');
  };

  if (loading && !analytics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress sx={{ color: neonBlue[500] }} />
      </Box>
    );
  }

  if (error && !analytics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const year = analytics?.year || selectedYear;
  const monthlyChart = analytics?.monthlyChart || [];
  const dailyChart = analytics?.dailyChart || [];
  const monthNumber = Number(selectedMonth);
  const yearNumber = Number(selectedYear);
  const periodLabel = analytics?.selectedPeriod?.label
    || `${MONTH_OPTIONS.find((m) => m.value === monthNumber)?.label || ''} ${yearNumber}`;
  const isCurrentMonth = analytics?.selectedPeriod?.isCurrentMonth ?? (
    yearNumber === new Date().getFullYear() && monthNumber === new Date().getMonth() + 1
  );
  const todayLabel = isCurrentMonth ? 'Today' : 'Today (n/a)';
  const monthLabel = periodLabel;
  const todayStats = analytics?.today || { received: 0, dispatched: 0, delivered: 0 };
  const monthStats = analytics?.month || { received: 0, dispatched: 0, delivered: 0 };

  return (
    <Box sx={{ mb: 3, width: '100%', maxWidth: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          background: `linear-gradient(135deg, ${neonBlue[600]} 0%, ${neonBlue[500]} 50%, ${shakespeare[500]} 100%)`,
          color: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {selectedCompanyName ? `${selectedCompanyName} Dashboard` : 'Overview'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: alpha('#fff', 0.85) }}>
              Track order activity and delivery trends
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
            <FormControl size="small" sx={filterControlSx}>
              <InputLabel>Month</InputLabel>
              <Select
                value={monthNumber}
                onChange={(event) => setSelectedMonth(Number(event.target.value))}
                label="Month"
              >
                {MONTH_OPTIONS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={filterControlSx}>
              <InputLabel>Year</InputLabel>
              <Select
                value={yearNumber}
                onChange={(event) => setSelectedYear(Number(event.target.value))}
                label="Year"
              >
                {yearOptions.map((yearOption) => (
                  <MenuItem key={yearOption} value={yearOption}>
                    {yearOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isSuperAdmin && (
              <FormControl size="small" sx={{ ...filterControlSx, minWidth: 220 }}>
                <InputLabel>Filter by Company</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label="Filter by Company"
                >
                  <MenuItem value="">
                    <em>All Companies</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 2, border: `1px solid ${alpha(neonBlue[500], 0.2)}` }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Paper>
      )}

      <SectionHeading title="Order Activity" subtitle={`${periodLabel} activity${isCurrentMonth ? ' vs today' : ''}`} />

      <Grid container spacing={3} sx={{ mb: 4, width: '100%' }}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            title="Orders Received"
            today={todayStats.received}
            month={monthStats.received}
            todayLabel={todayLabel}
            monthLabel={monthLabel}
            icon={InboxIcon}
            accent={neonBlue[500]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            title="Orders Dispatched"
            today={todayStats.dispatched}
            month={monthStats.dispatched}
            todayLabel={todayLabel}
            monthLabel={monthLabel}
            icon={LocalShippingIcon}
            accent={shakespeare[500]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            title="Orders Delivered"
            today={todayStats.delivered}
            month={monthStats.delivered}
            todayLabel={todayLabel}
            monthLabel={monthLabel}
            icon={CheckCircleOutlineIcon}
            accent={kepple[500]}
          />
        </Grid>
      </Grid>

      <SectionHeading title="Delivery Trends" subtitle={`Performance charts for ${year}`} />

      <Grid container spacing={3} sx={{ mb: 3, width: '100%' }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartPanel
            title="Monthly Orders vs Deliveries"
            subtitle="Bars = orders created, line = orders delivered"
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyChart} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(stormGrey[300], 0.6)} />
                <XAxis dataKey="month" tick={{ fill: stormGrey[600], fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: stormGrey[600], fontSize: 12 }} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Bar dataKey="orders" name="Orders" fill={chartBarColor} radius={[4, 4, 0, 0]} barSize={28} />
                <Line
                  type="monotone"
                  dataKey="delivered"
                  name="Delivered"
                  stroke={chartLineColor}
                  strokeWidth={3}
                  dot={{ r: 4, fill: chartLineColor }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartPanel
            title="Last 10 Days"
            subtitle={`Daily orders created vs delivered in ${periodLabel}`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dailyChart} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(stormGrey[300], 0.6)} />
                <XAxis dataKey="date" tick={{ fill: stormGrey[600], fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: stormGrey[600], fontSize: 12 }} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Bar dataKey="orders" name="Orders" fill={chartBarColor} radius={[4, 4, 0, 0]} barSize={22} />
                <Line
                  type="monotone"
                  dataKey="delivered"
                  name="Delivered"
                  stroke={chartLineColor}
                  strokeWidth={3}
                  dot={{ r: 4, fill: chartLineColor }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard;
